import { Component, Event, EventEmitter, h, Host, Prop } from "@stencil/core";
import { App, Service, QuacInfo } from "../../api/types";
import { getAnchorTarget, LinkTarget } from "../../lib/link-target";
import { ModalOpen } from "../../lib/modal";
import { zapierURL } from "../../api/config";
import { urlWithReferrer } from "../../lib/referrer";
import { urlWithQuACParams } from "../../lib/quac";
import { EllipsesIcon } from "../../lib/icons";

type AccessibleImage = {
  src: string;
  alt: string;
};

@Component({
  tag: "zapier-prefab-row",
  styleUrl: "zapier-prefab-row.css",
  shadow: true,
})
export class ZapierZapSuggestionRow {
  /** Open links in a new tab or the same tab */
  @Prop() linkTarget: LinkTarget = "new-tab";

  /** The Zap Template URL, either set by the user or Zap Template */
  @Prop() url?: string;

  /** The Zap status (on or off), used for the Zap Manager Element (ZME) */
  @Prop() zapState?: string;

  /** The Zap title, either set by the user or Zap Template */
  @Prop() zapTitle?: string;

  /** @deprecated The app services used in the Zap */
  @Prop() services?: Service[];

  /** @deprecated The first app being selected */
  @Prop() firstApp?: App;

  /** @deprecated The second app being selected */
  @Prop() secondApp?: App;

  /** The icons and app titles to display */
  @Prop() accessibleImages?: AccessibleImage[];

  /** Call to action text */
  @Prop() callToAction?: string;

  /** The HTTP referrer to use for links and events */
  @Prop() referrer = location.href;

  /**
   * [Facebook] Extra query string parameters so that Guided Setup / Editor can
   * render different experiences for these users on the WebIntent links
   */
  @Prop() fbEmbed?: string;

  /** User sign up information for QuAC */
  @Prop() quacInfo?: QuacInfo;

  /** Request to open a modal to the given URL */
  @Event() modalOpen!: EventEmitter<ModalOpen>;

  private get _title(): string {
    return this.zapTitle ?? "Create your own workflow";
  }

  private get _subtitle(): string {
    // TODO: Trim this for when the list gets long
    if (this.accessibleImages) {
      return this.accessibleImages.map((x) => x.alt).join(" + ");
    }
    if (this.firstApp && this.secondApp) {
      return `${this.firstApp.name} + ${this.secondApp.name}`;
    }
    // TODO: Trim this for when the list gets long
    if (this.services) {
      return this.services.map((s) => s.name).join(" + ");
    }
    return "";
  }

  private get _iconItems(): AccessibleImage[] {
    if (this.accessibleImages) {
      return this.accessibleImages;
    }
    if (this.services) {
      return this.services.map((service) => ({
        src: service.images?.url_32x32,
        alt: service.name,
      }));
    }
    if (this.firstApp && this.secondApp) {
      return [this.firstApp, this.secondApp]
        .filter((x) => x)
        .map((app) => ({
          src: app.images?.url_32x32,
          alt: app.name,
        }));
    }
    return [];
  }

  private get _href() {
    if (this.url) {
      const refURL = new URL(this.url);
      // If fb_embed exists on the ZTE, add to WebIntent links
      if (this.fbEmbed) {
        refURL.searchParams.set("fb_embed", this.fbEmbed);
      }
      return refURL.href;
    }
    return new URL("/webintent/create-zap", zapierURL).href;
  }

  // Card components are problematic from an HTML perspective because you're not
  // allowed to nest `<a>` tags inside each other. I copied the approach from
  // this website of using `::after` to increase the size of the title's
  // clickable area to fit the entire card, and then positioning any nested
  // links above it.
  //
  // https://inclusive-components.design/cards/
  //
  // Sage Fennel (2023-06-13)
  render() {
    const newWindowText =
      this.linkTarget === "new-tab" && "(opens in new window)";
    let href = urlWithReferrer(this._href, this.referrer);
    href = urlWithQuACParams(href, this.quacInfo);
    const iconItems = this._iconItems;
    const maxIcons = 3;

    return (
      <Host>
        <div class="prefab-row-host-container">
          <div
            class="zap"
            data-zap-state={Boolean(this.zapState)}
            data-cta={Boolean(this.callToAction)}
          >
            {iconItems.length <= maxIcons ? (
              <div class="icon-container">
                {iconItems.map((item) => (
                  <div class="icon">
                    <img src={item.src} alt="" />
                  </div>
                ))}
              </div>
            ) : (
              <div class="icon-container">
                {iconItems.slice(0, maxIcons - 1).map((item) => (
                  <div class="icon">
                    <img src={item.src} alt="" />
                  </div>
                ))}
                <EllipsesIcon />
                {iconItems.slice(-1).map((item) => (
                  <div class="icon">
                    <img src={item.src} alt="" />
                  </div>
                ))}
              </div>
            )}
            <h3 class="body">
              <a
                aria-label={`${this._title} using ${this._subtitle} apps ${newWindowText}`}
                class="title-link"
                data-cta={Boolean(this.callToAction)}
                href={href}
                target={getAnchorTarget(this.linkTarget)}
                onClick={(event) => {
                  if (this.linkTarget === "modal") {
                    event.preventDefault();
                    event.stopPropagation();
                    this.modalOpen.emit({ url: href });
                  }
                }}
                referrerPolicy="no-referrer-when-downgrade"
              >
                {this._title}
              </a>
            </h3>
            {this.zapState && (
              <span
                aria-label={`Zap is ${this.zapState}`}
                class="zap-state"
                data-enabled={this.zapState === "on"}
                role="note"
              >
                {this.zapState}
              </span>
            )}
            <slot name="footer-start" />
            <slot name="footer-end" />
            <div class="button cta" hidden={!this.callToAction}>
              {this.callToAction}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
